<template>
  <v-container>
    <v-btn v-if="session_id && notProd" @click="resetViewProgress"
      >Сбросить прогресс просмотра текущей сессии</v-btn
    >
    <div v-if="playerError" class="player-error">
      <p class="player-error__title">Видеоплеер недоступен</p>
      <p class="player-error__text">
        Не удалось загрузить видеоплеер. Попробуйте перезагрузить страницу. Если
        используете VPN — отключите его и перезагрузите страницу.
      </p>
    </div>
    <kinescope-player
      v-else
      ref="kinescope"
      :video-id="`${video_data}?t=${this.startTime}`"
      height="300vh"
      @play="handlePlay"
      @pause="handlePause"
      @ended="handleEnded"
      @ready="handleReady"
      @time-update="handleTimeUpdate"
      @error="handlePlayerError"
    ></kinescope-player>
  </v-container>
</template>

<script>
import { KinescopePlayer } from "@kinescope/vue-kinescope-player";

export default {
  name: "NSPlayer",
  props: ["video_data", "session_id", "view_progress"],
  components: { KinescopePlayer },
  emits: ["videoEnded", "updateViewProgress"],
  data() {
    return {
      playing: false,
      saveProgressToDBInterval: undefined,
      notProd: !process.env.VUE_APP_PRODUCT_DATA_PATH.includes("account"),
      currentTime: 0,
      startTime: 0,
      isTimeChangedManually: false,
      lastRecordedTime: 0,
      currentPercent: 0,
      playerError: false,
    };
  },
  beforeMount() {
    // Интервальное сохранение прогресса воспроизведения в БД
    this.saveProgressToDBInterval = setInterval(() => {
      if (this.playing) {
        this.saveProgressToDB();
      }
    }, 5000);

    // Восстановление прогресса воспроизведения видео
    if (this.session_id) {
      const currentTimeFromDatabase = this.view_progress ?? 0;
      let currentTime = currentTimeFromDatabase ?? 0;
      this.currentTime = currentTime;
      this.startTime = Math.round(currentTime);
    }
  },
  methods: {
    handleReady() {
      this.lastRecordedTime = this.currentTime;

      if (this.$refs.kinescope && this.$refs.kinescope.player) {
        this.$refs.kinescope.player.seekTo(this.startTime).catch((error) => {
          console.error("Ошибка при установке времени воспроизведения:", error);
        });
      }
    },
    handlePlay() {
      this.playing = true;
    },
    handlePause() {
      if (this.$refs.kinescope && this.$refs.kinescope.player) {
        this.$refs.kinescope.player
          ?.getCurrentTime()
          .then((currentTime) => (this.currentTime = currentTime))
          .catch((error) => {
            console.error("Ошибка при получении текущего времени:", error);
          });
      }

      this.playing = false;
      this.saveProgressToDB();
    },
    handleEnded() {
      this.played = false;
      this.saveProgressToDB();
      this.$emit("videoEnded", {
        currentTime: this.lastRecordedTime,
        percent: this.currentPercent,
      });
    },
    saveProgressToDB() {
      if (
        this.session_id &&
        this.$refs.kinescope &&
        this.$refs.kinescope.player
      ) {
        this.$refs.kinescope.player
          .getCurrentTime()
          .then((currentTime) => {
            this.$emit("updateViewProgress", {
              currentTime,
              percent: this.currentPercent,
            });
          })
          .catch((error) => {
            console.error("Ошибка при получении текущего времени:", error);
          });
      }
    },
    resetViewProgress() {
      localStorage.removeItem(this.session_id);
      this.$emit("updateViewProgress", 0);
      if (this.$refs.kinescope && this.$refs.kinescope.player) {
        this.$refs.kinescope.player.seekTo(0).catch((error) => {
          console.error("Ошибка при сбросе прогресса:", error);
        });
      }
    },
    closeNSPlayer() {
      if (this.$refs.kinescope && this.$refs.kinescope.player) {
        this.$refs.kinescope.player.pause().catch(() => {});
      }
      this.currentTime = 0;
      this.lastRecordedTime = 0;
    },
    destroyPlayer() {
      if (this.$refs.kinescope && this.$refs.kinescope.player) {
        try {
          this.$refs.kinescope.player.destroy();
        } catch (error) {
          console.error("Ошибка при уничтожении плеера:", error);
        }
      }
    },
    handlePlayerError(error) {
      console.error("Ошибка инициализации плеера:", error);
      this.playerError = true;
      const errorMessage =
        error?.message ||
        (typeof error === "object" ? JSON.stringify(error) : String(error));
      const videoId = this.video_data;
      fetch(`${process.env.VUE_APP_SERVER_URI}/v1/videoOrder/player-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderDataId: this.session_id,
          videoId,
          errorMessage,
          source: "b2b",
        }),
      }).catch(() => {});
    },
    handleTimeUpdate(timeData) {
      if (!timeData || typeof timeData.currentTime !== "number") {
        return;
      }

      if (this.isTimeChangedManually) {
        this.isTimeChangedManually = false;
        return;
      }

      if (timeData.percent !== undefined) {
        this.currentPercent = timeData.percent;
      }

      const delta = Math.abs(timeData.currentTime - this.lastRecordedTime);

      if (delta > 1 && this.$refs.kinescope && this.$refs.kinescope.player) {
        this.isTimeChangedManually = true;
        this.$refs.kinescope.player
          .seekTo(this.lastRecordedTime)
          .catch((error) => {
            console.error(
              "Ошибка при восстановлении времени воспроизведения:",
              error
            );
            this.isTimeChangedManually = false;
          });
      } else {
        this.lastRecordedTime = timeData.currentTime;
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.saveProgressToDBInterval);
    this.closeNSPlayer();
    this.destroyPlayer();
  },
};
</script>

<style scoped lang="scss">
.player-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  min-height: 200px;

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #4c5456;
    margin-bottom: 8px;
  }

  &__text {
    font-size: 14px;
    color: #5f7173;
  }
}
</style>
